import { useState } from 'react';

import { DEFAULT_QUALITY_WINDOW_DAYS } from '@clm/shared-types';

import { requireSurface } from '../routes/surfaces';
import { AliasesPanel } from './quality/AliasesPanel';
import { BoostsPanel } from './quality/BoostsPanel';
import { JudgementLedgerPanel } from './quality/JudgementLedgerPanel';
import { ObligationOwnersPanel } from './quality/ObligationOwnersPanel';
import { OutboundHealthPanel } from './quality/OutboundHealthPanel';
import { SearchWeaknessPanel } from './quality/SearchWeaknessPanel';
import { StabilityPanel } from './quality/StabilityPanel';
import { StaleJudgementsPanel } from './quality/StaleJudgementsPanel';

/**
 * Search quality — the quality dashboards, bound (CLM-40; O-1, O-6, FR-26,
 * FR-27, NFR-11, NFR-15). The last wave-3 stub, un-stubbed.
 *
 * ## One surface, independent panels
 *
 * Four read-only panels, each behind its own `GET /v1/quality/*` endpoint and
 * its own state: a 403 or a 501 on one leaves the other three live, and each
 * list pages on its own cursor. The header, the Window control and the four
 * headings render **outside** every panel branch, so the page is never blank
 * and never a false "all healthy".
 *
 * ## Nothing here computes a number
 *
 * Every figure rendered is a wire field — one named SQL aggregate in
 * `apps/api/src/data/quality-queries.ts` — and no two panels' numbers are fused
 * (the `NeedsAttention` rule). O-6: identical inputs return identical bodies.
 *
 * ## Window
 *
 * One value shared by the two windowed panels (search weakness, outbound
 * health). A change remounts both, which resets their cursor stacks: cursors
 * are minted per filter set and one from another window is a 400.
 *
 * The section order is the critique's (C1): search first, the two ops panels
 * last. CLM-39 mounts its Judgements, Boosts and Stability panels at the slot
 * marked below, in that order.
 */

const WINDOWS = [7, 30, 90] as const;

export function SearchQuality() {
  const surface = requireSurface('quality');
  const [windowDays, setWindowDays] = useState<number>(DEFAULT_QUALITY_WINDOW_DAYS);

  return (
    <>
      <div className="hd">
        <div>
          <h2>{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <fieldset className="qwindow">
        <legend>Window</legend>
        <label className="check">
          Days
          <select
            aria-label="Window in days"
            value={windowDays}
            onChange={(event) => {
              setWindowDays(Number(event.target.value));
            }}
          >
            {WINDOWS.map((days) => (
              <option key={days} value={days}>
                {days}
              </option>
            ))}
          </select>
        </label>
        <span className="mut">Applies to Search weakness and Outbound health. Changing it restarts their paging.</span>
      </fieldset>

      <SearchWeaknessPanel key={`w-${String(windowDays)}`} windowDays={windowDays} />
      <StaleJudgementsPanel />
      {/* CLM-39 slot: JudgementLedgerPanel · BoostsPanel · StabilityPanel mount here, in that order */}
      <JudgementLedgerPanel />
      <BoostsPanel />
      <StabilityPanel />
      <AliasesPanel />
      <OutboundHealthPanel key={`o-${String(windowDays)}`} windowDays={windowDays} />
      <ObligationOwnersPanel />
    </>
  );
}
